import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BellOff,
  BookOpen,
  Calendar,
  ChevronRight,
  Circle,
  CircleCheck,
  Contact,
  HeartHandshake,
  Loader2,
  Lock,
  MessageCircle,
  PenLine,
  Route,
  Shield,
  Sparkles,
  Sun,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LogoMark } from "@/components/logo";
import { NoLiteratureNote, StepReadings } from "@/components/recovery/step-readings";
import {
  ApiError,
  getStep,
  getStep8Rows,
  startStep,
  updateStep,
  updateStep8Row,
  type Step8Person,
  type Step8Row,
  type Step9Outcome,
  type StepData,
} from "@/lib/api";
import { decryptRow, encryptRow } from "@/lib/step8-crypto";
import { shortDate } from "@/lib/format";

/**
 * Step 9 — the amends themselves (Step 9 PRD + design canvas). A real-world
 * act: the app holds the Step 8 list and quietly records what happened, per
 * person. The Step 5 acknowledgement archetype, applied per row.
 *
 * It builds NO new list (§2): outcomes are written onto the same encrypted
 * step8_rows, so every Step 8 rule is inherited — E2E encrypted, private to
 * the author, no sponsor visibility, mutable, user-deletable.
 *
 * What this screen must never grow (§1, §4, §6): letter drafting, message
 * composing, contact lookup, scheduling, reminders, AI of any kind, or any
 * assessment of whether an amends would cause harm. The app records the
 * decision the person reached with their sponsor — it never reaches one.
 */

/** The five outcomes (PRD §3; owner may re-word). None is ranked. */
const OUT: Record<Step9Outcome, { label: string; help: string; icon: typeof Circle }> = {
  notyet: {
    label: "Not yet",
    help: "The resting state. No clock on it.",
    icon: Circle,
  },
  planned: {
    label: "Planned with my sponsor",
    help: "Worked out together, not yet done.",
    icon: Calendar,
  },
  made: {
    label: "Amends made",
    help: "It happened. Nothing about what was said is kept.",
    icon: CircleCheck,
  },
  another: {
    label: "Made in another way",
    help: "Indirect or living amends, where a conversation isn't possible or right.",
    icon: Route,
  },
  notmaking: {
    label: "Not making this one",
    help: "Decided with your sponsor, because it would injure someone. A complete answer.",
    icon: Shield,
  },
};
const OUT_KEYS: Step9Outcome[] = ["notyet", "planned", "made", "another", "notmaking"];
/** Outcomes that are settled — used for tone only, never to compute progress. */
const SETTLED: Step9Outcome[] = ["made", "another", "notmaking"];

type Decrypted = Step8Row & { person: Step8Person | null };

type Screen =
  | { s: "overview" }
  | { s: "reading" }
  | { s: "list" }
  | { s: "outcome"; rowId: string }
  | { s: "privacy" }
  | { s: "ready" }
  | { s: "close" };

function EncryptedTag() {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Lock className="h-[13px] w-[13px]" strokeWidth={2} /> Encrypted · only you
    </span>
  );
}

function Note({ icon: Icon = Sun, children }: { icon?: typeof Sun; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
      <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
      <p className="text-[13px] leading-relaxed text-foreground">{children}</p>
    </div>
  );
}

/**
 * Same anti-verdict rule as Step 8's willingness: shape and label carry the
 * state, the primary tint marks only the current selection, and none of the
 * check-in's Green/Yellow/Red appears. "Not making this one" is styled exactly
 * like the rest — it is a completion, not a gap (§4).
 */
function OutPill({ state }: { state: Step9Outcome }) {
  const o = OUT[state];
  const Icon = o.icon;
  const settled = SETTLED.includes(state);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
        settled
          ? "border-primary bg-primary/[0.06] text-primary"
          : "border-border bg-muted text-muted-foreground"
      }`}
    >
      <Icon className="h-[13px] w-[13px]" strokeWidth={2} /> {o.label}
    </span>
  );
}

function OutPicker({
  value,
  onChange,
}: {
  value: Step9Outcome;
  onChange: (v: Step9Outcome) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {OUT_KEYS.map((k) => {
        const o = OUT[k];
        const Icon = o.icon;
        const on = k === value;
        return (
          <button
            key={k}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(k)}
            className={`flex min-h-[44px] items-start gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
              on ? "border-2 border-primary bg-primary/[0.06]" : "border-border"
            }`}
          >
            <span className={`mt-0.5 shrink-0 ${on ? "text-primary" : "text-muted-foreground"}`}>
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>
            <span>
              <span className="block text-sm font-medium text-foreground">{o.label}</span>
              <span className="block text-xs leading-snug text-muted-foreground">{o.help}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function Step9() {
  const navigate = useNavigate();
  const [data, setData] = useState<StepData | null>(null);
  const [rows, setRows] = useState<Decrypted[]>([]);
  const [screen, setScreen] = useState<Screen>({ s: "overview" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [readOn, setReadOn] = useState("");

  async function load() {
    try {
      // The list is Step 8's — Step 9 reads and writes the same encrypted rows.
      const [d, r] = await Promise.all([getStep(9), getStep8Rows()]);
      setData(d);
      setReadOn(d.work?.reading_read_on ?? "");
      setRows(
        await Promise.all(
          r.rows.map(async (row) => {
            const dec = await decryptRow(row.ciphertext);
            return {
              ...row,
              person: dec ? (JSON.parse(dec.v ?? "{}") as Step8Person) : null,
            };
          })
        )
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't load Step 9.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readable = rows.filter((r) => r.person !== null);
  const total = rows.length;
  const lastUpdated = total
    ? rows.reduce((m, r) => (r.updated_at > m ? r.updated_at : m), rows[0].updated_at)
    : null;

  /**
   * Where the list stands, in prose. NOT a tally, NOT a threshold, and never
   * rendered as a bar or a percentage (§5) — no count is framed as a score.
   */
  const standing = useMemo(() => {
    const c: Record<Step9Outcome, number> = {
      notyet: 0,
      planned: 0,
      made: 0,
      another: 0,
      notmaking: 0,
    };
    for (const r of readable) c[r.person?.outcome ?? "notyet"]++;
    const parts: string[] = [];
    const n = (k: number, one: string, many: string) => (k === 1 ? one : many.replace("%", `${k}`));
    if (c.made) parts.push(n(c.made, "One amends made", "% amends made"));
    if (c.another) parts.push(n(c.another, "one made another way", "% made another way"));
    if (c.planned) parts.push(n(c.planned, "one planned", "% planned"));
    if (c.notmaking) parts.push(n(c.notmaking, "one you decided against", "% you decided against"));
    if (c.notyet) parts.push(n(c.notyet, "one you haven't come to yet", "% you haven't come to yet"));
    if (!parts.length) return "";
    const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const rest = parts.slice(1);
    return rest.length
      ? `${[first, ...rest.slice(0, -1)].join(", ")}${rest.length > 1 ? "," : ""} and ${rest[rest.length - 1]}.`
      : `${first}.`;
  }, [readable]);

  async function begin() {
    setBusy(true);
    try {
      if (!data?.work) await startStep(9);
      await updateStep(9, { current_phase: data?.work ? "amends" : "reading" });
      await load();
      setScreen(data?.work ? { s: "list" } : { s: "reading" });
    } catch {
      setError("Couldn't start Step 9.");
    } finally {
      setBusy(false);
    }
  }

  /** Save an outcome onto the Step 8 row, leaving everything else as written. */
  async function saveOutcome(rowId: string, patch: Partial<Step8Person>) {
    const row = rows.find((r) => r.id === rowId);
    if (!row?.person) return;
    setBusy(true);
    try {
      const person: Step8Person = { ...row.person, ...patch };
      const ciphertext = await encryptRow({ v: JSON.stringify(person) });
      const updated = await updateStep8Row(rowId, ciphertext);
      setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, ...updated, person } : r)));
      setScreen({ s: "list" });
    } catch {
      setError("Couldn't save. Nothing you wrote left this screen — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function markReady() {
    setBusy(true);
    try {
      await updateStep(9, { status: "ready", ready_to_talk: true, current_phase: "close" });
      await load();
      setScreen({ s: "close" });
    } catch {
      setError("Couldn't mark Step 9 ready.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => navigate("/steps")}>Back to Steps</Button>
      </div>
    );
  }
  if (!data) return null;
  if (!data.available) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <Lock className="h-8 w-8 text-muted-foreground" strokeWidth={2} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Step 9 isn't open yet</h1>
          <p className="mx-auto mt-2 max-w-[300px] text-sm leading-relaxed text-muted-foreground">
            It opens when you mark Step 8 ready. It works from the list you make there.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/steps")}>Back to Steps</Button>
      </div>
    );
  }

  const { step } = data;
  const header = (title?: string, opts?: { back?: () => void; saved?: boolean }) => (
    <div className="flex items-center gap-2 px-4 pb-2 pt-6">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Back"
        onClick={opts?.back ?? (() => navigate("/steps"))}
      >
        <ArrowLeft className="h-5 w-5" strokeWidth={2} />
      </Button>
      <span className="flex-1 truncate text-sm font-semibold text-foreground">{title}</span>
      {opts?.saved ? (
        <span className="pr-2 text-xs text-muted-foreground">Saved</span>
      ) : (
        <span className="pr-2"><EncryptedTag /></span>
      )}
    </div>
  );

  /* --------- no list: say it plainly, no empty ceremony (§2) --------- */
  if (total === 0 && (screen.s === "list" || screen.s === "overview" || screen.s === "ready")) {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 9")}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Nine{step.principle ? ` · ${step.principle}` : ""}
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              Step 9 works from your Step 8 list.
            </h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">
            There's nobody on it yet, so there's nothing here to hold. Go back and add whoever
            comes to mind first — this screen will be waiting.
          </p>
          <Card interactive onClick={() => navigate("/steps/8")}>
            <CardContent className="flex items-center gap-3 p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Users className="h-5 w-5" strokeWidth={2} />
              </span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Step 8 — your list</p>
                <p className="mt-0.5 text-[12.5px] text-muted-foreground">Nobody listed yet</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
            </CardContent>
          </Card>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            If you deleted the list on purpose, that's fine too. Step 9 stays open and empty until
            there's something to put in it.
          </p>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth onClick={() => navigate("/steps/8")}>
              Go to Step 8
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate("/steps")}>
              Return to Steps
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------ overview ------------------------------ */
  if (screen.s === "overview") {
    const resumed = !!data.work;
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header()}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Nine{step.principle ? ` · ${step.principle}` : ""}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-foreground">Step 9</h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">{step.intro_text}</p>
          {resumed ? (
            <Card>
              <CardContent className="flex flex-col gap-2 px-4 py-3.5">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Where you are
                </p>
                <p className="flex items-center gap-2 text-sm text-foreground">
                  <Users className="h-3.5 w-3.5" strokeWidth={2} />
                  {total} {total === 1 ? "person" : "people"} on your list
                  {lastUpdated ? ` · last updated ${shortDate(lastUpdated)}` : ""}
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  No order, no schedule. Open the list when something has happened and mark it
                  then.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex flex-col gap-2.5">
                {(
                  [
                    [Users, "Your Step 8 list", "The same people, the same rows. Nothing new to write."],
                    [
                      HeartHandshake,
                      "Planned with your sponsor",
                      "Who, when, and how — decided out loud, between the two of you.",
                    ],
                    [
                      Shield,
                      "Some you won't make",
                      "Where it would injure them or someone else. That's the step, not a gap in it.",
                    ],
                  ] as [typeof Users, string, string][]
                ).map(([Icon, t, d]) => (
                  <div key={t} className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{t}</p>
                      <p className="text-xs leading-snug text-muted-foreground">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
              {step.privacy_note && <Note icon={Lock}>{step.privacy_note}</Note>}
              {step.supporting_note && <Note>{step.supporting_note}</Note>}
            </>
          )}
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            Take each one to your sponsor before you take it to the person. Some amends are
            complicated, and a few are best not made at all.
          </p>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth disabled={busy} onClick={begin}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : resumed ? (
                "Open My List"
              ) : (
                "Begin Step 9"
              )}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setScreen({ s: "privacy" })}>
              How This Is Stored
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------ reading ------------------------------ */
  if (screen.s === "reading") {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 9", { back: () => setScreen({ s: "overview" }), saved: true })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-3.5 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Nine · Reading
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">The reading</h1>
          </div>
          <StepReadings citations={data.citations} />
          {data.citations.length === 0 && <NoLiteratureNote />}
          {step.reading_note && <Note icon={BookOpen}>{step.reading_note}</Note>}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              When did you read it?{" "}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Input
              placeholder="e.g. August 24"
              value={readOn}
              onChange={(e) => setReadOn(e.target.value)}
            />
          </div>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button
              size="xl"
              fullWidth
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await updateStep(9, {
                    reading_read_on: readOn.trim() || null,
                    reading_acknowledged: true,
                    current_phase: "amends",
                  });
                  await load();
                  setScreen({ s: "list" });
                } catch {
                  setError("Couldn't save your progress. Please try again.");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Continue"}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate("/steps")}>
              Save &amp; Exit
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* -------------------------------- the list -------------------------------- */
  if (screen.s === "list") {
    const untouched = readable.every((r) => (r.person?.outcome ?? "notyet") === "notyet");
    const marked = data.work?.status === "ready" || data.work?.status === "worked";
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 9", { back: () => setScreen({ s: "overview" }) })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-3 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Your list
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              {untouched
                ? `The same ${total === 1 ? "person" : `${total} people`}.`
                : `${total} ${total === 1 ? "person" : "people"}, as things stand.`}
            </h1>
          </div>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            {untouched
              ? "Every row starts at Not yet. Mark one when something has actually happened, and only then."
              : "Open a person to change where things stand. Anything can move back as well as forward."}
          </p>
          <div className="flex flex-col gap-2">
            {rows.map((r) => (
              <Card
                key={r.id}
                interactive={!!r.person}
                onClick={r.person ? () => setScreen({ s: "outcome", rowId: r.id }) : undefined}
              >
                <CardContent className="flex items-center gap-3 px-4 py-3.5">
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {r.person ? r.person.who.trim() || "Unnamed" : "Written on another device"}
                    </p>
                    {r.person?.harm.trim() && (
                      <p className="truncate text-xs text-muted-foreground">{r.person.harm}</p>
                    )}
                    {r.person && (
                      <div className="flex flex-wrap items-center gap-2">
                        <OutPill state={r.person.outcome ?? "notyet"} />
                        {r.person.outcome_note?.trim() && (
                          <span className="truncate text-[11.5px] text-muted-foreground">
                            {r.person.outcome_note}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {r.person && (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {untouched && (
            <Note>
              Nothing is expected of you today. The list looking exactly like this in a year is a
              real answer.
            </Note>
          )}
          <div className="mt-auto flex flex-col items-start gap-2.5 pt-5">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-[13px] w-[13px]" strokeWidth={2} /> Encrypted on this device ·
              never shared with your sponsor
            </span>
            <button
              type="button"
              className="text-[13px] text-muted-foreground underline underline-offset-2"
              onClick={() => setScreen({ s: "privacy" })}
            >
              How Step 9 Is Stored
            </button>
          </div>
          <div className="flex flex-col gap-2 pt-4">
            {!marked && (
              <Button size="xl" fullWidth onClick={() => setScreen({ s: "ready" })}>
                Mark Step 9 Ready
              </Button>
            )}
            <Button
              variant={marked ? "default" : "ghost"}
              size={marked ? "xl" : undefined}
              fullWidth
              onClick={() => navigate("/steps")}
            >
              Return to Steps
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------- one person: where things stand ------------------- */
  if (screen.s === "outcome") {
    const row = rows.find((r) => r.id === screen.rowId);
    if (!row?.person) {
      setScreen({ s: "list" });
      return null;
    }
    return (
      <OutcomeScreen
        key={row.id}
        person={row.person}
        busy={busy}
        header={header}
        onBack={() => setScreen({ s: "list" })}
        onSave={(patch) => void saveOutcome(row.id, patch)}
      />
    );
  }

  /* ------------------------------ privacy ------------------------------ */
  if (screen.s === "privacy") {
    const facts: [typeof Lock, string, string][] = [
      [
        Lock,
        "Encrypted on this device",
        "Outcomes and notes are held the same way the list is. We can't read any of it.",
      ],
      [
        Users,
        "Never shared with your sponsor",
        "There's no sharing control for the amends list. You talk about it in person, as you always have.",
      ],
      [
        Sparkles,
        "No AI, at all",
        "No drafted letters, no suggested wording, no coaching on how to approach someone.",
      ],
      [
        Contact,
        "No contact features, deliberately",
        "No email, SMS, contacts or scheduling. The app can never be the way you reach someone you harmed.",
      ],
      [
        BellOff,
        "No reminders, ever",
        "Nothing will nudge you about an amends, and nothing will tell you how many are outstanding.",
      ],
      [
        PenLine,
        "Yours to change and delete",
        "Every outcome moves in any direction, any time. Deleting the Step 8 list takes Step 9's marks with it.",
      ],
    ];
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("How Step 9 is stored", {
          back: () => setScreen(data.work ? { s: "list" } : { s: "overview" }),
        })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <p className="text-[15px] leading-relaxed text-foreground">
            Step 9 writes onto your Step 8 rows, and inherits every rule they came with.
          </p>
          {facts.map(([Icon, t, d]) => (
            <div key={t} className="flex items-start gap-3">
              <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-[17px] w-[17px]" strokeWidth={2} />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground">{t}</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{d}</p>
              </div>
            </div>
          ))}
          <div className="mt-auto pt-4">
            <Button
              size="xl"
              fullWidth
              onClick={() => setScreen(data.work ? { s: "list" } : { s: "overview" })}
            >
              {data.work ? "Back to My List" : "Back to Step 9"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------- ready ------------------------------- */
  if (screen.s === "ready") {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 9", { back: () => setScreen({ s: "list" }), saved: true })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Nine · Ready
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              Ready, with the list still open?
            </h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">
            Step 9 doesn't end. Marking it ready says you've worked it and you're living it — rows
            can sit at Not yet for years and this still means what it says. It's your call, made
            with your sponsor, not something the app works out from your list.
          </p>
          {standing && (
            <Card>
              <CardContent className="flex flex-col gap-2.5 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Where the list stands
                </p>
                <p className="text-sm leading-relaxed text-foreground">{standing}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Not a tally, and not a threshold. It's here so you don't have to hold it in your
                  head.
                </p>
              </CardContent>
            </Card>
          )}
          <div className="flex items-center gap-3 rounded-lg border-2 border-primary bg-primary/[0.06] px-4 py-3.5">
            <CircleCheck className="h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
            <p className="text-sm font-medium text-foreground">Mark Step 9 ready</p>
          </div>
          <Note>
            The list stays exactly where it is afterwards. Keep marking things as they happen —
            some of them will happen years from now.
          </Note>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth disabled={busy} onClick={() => void markReady()}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : (
                "Let My Sponsor Know"
              )}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setScreen({ s: "list" })}>
              Not Yet
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------- close: the bridge to Step 10 (§8) ------------------- */
  return (
    <div className="flex min-h-dvh flex-col px-6 pb-8 pt-6">
      <div className="animate-fade-rise flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <LogoMark className="h-14 w-14 animate-scale-in" />
        <h1 className="text-2xl font-bold text-foreground">You've marked Step 9 ready.</h1>
        <p className="mx-auto max-w-[302px] text-[15px] leading-relaxed text-muted-foreground">
          The list stays open and stays yours. Come back and mark things whenever they happen.
        </p>
        <p className="mx-auto max-w-[302px] text-[15px] leading-relaxed text-muted-foreground">
          Step 10 is the daily inventory — the check-in you've been doing this whole time. You're
          already keeping it.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Button size="xl" fullWidth onClick={() => navigate("/checkin")}>
          Go to Today's Check-In
        </Button>
        <Button variant="ghost" fullWidth onClick={() => navigate("/steps")}>
          Return to Steps
        </Button>
      </div>
    </div>
  );
}

/**
 * One person's outcome. When the row was flagged in Step 8 ("talk this one
 * through"), that marker is surfaced as context — never as a suggestion about
 * what to decide. When the outcome is the carve-out, the screen says outright
 * that it is recorded as complete (§4).
 */
function OutcomeScreen({
  person,
  busy,
  header,
  onBack,
  onSave,
}: {
  person: Step8Person;
  busy: boolean;
  header: (title?: string, opts?: { back?: () => void; saved?: boolean }) => React.ReactNode;
  onBack: () => void;
  onSave: (patch: Partial<Step8Person>) => void;
}) {
  const [outcome, setOutcome] = useState<Step9Outcome>(person.outcome ?? "notyet");
  const [note, setNote] = useState(person.outcome_note ?? "");
  const who = person.who.trim() || "this person";
  const firstName = who.split(/[\s—–-]+/)[0] || who;
  const carveOut = outcome === "notmaking";

  return (
    <div className="flex min-h-dvh flex-col pb-8">
      {header(who, { back: onBack, saved: true })}
      <div className="animate-fade-rise flex flex-1 flex-col gap-3.5 px-6 pt-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Step Nine · This person
          </p>
          <h1 className="mt-1 text-xl font-semibold text-foreground">
            {carveOut ? "Not making this one" : `Where do things stand with ${firstName}?`}
          </h1>
        </div>

        {carveOut ? (
          <p className="text-[15px] leading-relaxed text-foreground">
            A direct amends here would do more harm than good. That decision — made with your
            sponsor — is the step working, not a corner cut.
          </p>
        ) : (
          person.harm.trim() && (
            <Card className="bg-secondary">
              <CardContent className="px-4 py-3.5">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  From your Step 8 list
                </p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                  {person.harm}
                </p>
              </CardContent>
            </Card>
          )
        )}

        {person.flagged && !carveOut && (
          <div className="flex items-start gap-3 rounded-lg border-2 border-primary bg-primary/[0.06] px-4 py-3.5">
            <MessageCircle className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
            <div>
              <p className="text-[13.5px] font-semibold text-foreground">
                Marked to talk through with your sponsor
              </p>
              <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted-foreground">
                You put this marker here in Step 8. Whatever the two of you land on gets recorded
                below — the app has no view on it.
              </p>
            </div>
          </div>
        )}

        <OutPicker value={outcome} onChange={setOutcome} />

        {carveOut && (
          <Note icon={HeartHandshake}>
            The app doesn't judge which amends would cause harm — it has no criteria and won't ever
            suggest one. You brought this from a conversation with your sponsor; all it does is
            remember what you decided. Recorded as complete: nothing will count it as outstanding,
            and nothing will ask you about it again.
          </Note>
        )}

        <div>
          <label className="mb-0.5 block text-[13px] font-semibold text-foreground">
            A private note <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <p className="mb-1.5 text-xs leading-snug text-muted-foreground">
            Enough to remember where things are. Not what was said.
          </p>
          <Textarea
            rows={2}
            placeholder="In your own words…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {carveOut
            ? "Changeable, like everything else here. If the two of you see it differently later, move it."
            : "Change it whenever things change. Anything can move back as well as forward."}
        </p>

        <div className="mt-auto flex flex-col gap-2 pt-4">
          <Button
            size="xl"
            fullWidth
            disabled={busy}
            onClick={() => onSave({ outcome, outcome_note: note.trim() })}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Save"}
          </Button>
          <Button variant="ghost" fullWidth onClick={onBack}>
            Leave It As It Is
          </Button>
        </div>
      </div>
    </div>
  );
}
